import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { useMyAllTags, createTag } from './api';
import type { Tag } from './api';
import { useDialog } from '../ui/Dialogs';
import { useAuth } from '../auth/AuthProvider';

interface Props {
  selected: Tag[];
  onChange: (next: Tag[]) => void;
}

const DEFAULT_COLOR = '#1d4ed8';

export default function TagSelector({ selected, onChange }: Props) {
  const { profile } = useAuth();
  const { alert } = useDialog();
  const { tags: allTags, refetch } = useMyAllTags(profile?.id);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Captura: dentro de modales que paran la propagación del mousedown
    // (p. ej. ReminderForm), un listener en burbujeo nunca llegaría aquí y el
    // popover quedaría atrapado sin poder cerrarse al pinchar fuera.
    document.addEventListener('mousedown', onClick, true);
    return () => document.removeEventListener('mousedown', onClick, true);
  }, [open]);

  const selectedIds = new Set(selected.map((t) => t.id));
  const filtered = allTags
    .filter((t) => !selectedIds.has(t.id))
    .filter((t) => t.nombre.toLowerCase().includes(query.trim().toLowerCase()));

  const exactMatch = allTags.find(
    (t) => t.nombre.toLowerCase() === query.trim().toLowerCase(),
  );

  const handleSelect = (tagId: string) => {
    const tag = allTags.find((t) => t.id === tagId);
    if (!tag) return;
    onChange([...selected, { id: tag.id, owner_id: profile!.id, nombre: tag.nombre, color: tag.color }]);
    setQuery('');
    inputRef.current?.focus();
  };

  const handleRemove = (tagId: string) => {
    onChange(selected.filter((t) => t.id !== tagId));
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name || !profile?.id) return;
    setCreating(true);
    try {
      const created = await createTag({ nombre: name, color: DEFAULT_COLOR }, profile.id);
      onChange([...selected, created]);
      setQuery('');
      await refetch();
      inputRef.current?.focus();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo crear la tag', message: msg });
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const first = filtered[0];
      if (first) {
        handleSelect(first.id);
      } else if (query.trim() && !exactMatch) {
        handleCreate();
      }
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      const last = selected[selected.length - 1];
      if (last) handleRemove(last.id);
    }
  };

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <div className="st-field" style={styles.chipsRow}>
        {selected.map((t) => (
          <span key={t.id} style={{ ...styles.chip, background: `${t.color}1a`, color: t.color, borderColor: t.color }}>
            {t.nombre}
            <button
              type="button"
              onClick={() => handleRemove(t.id)}
              style={{ ...styles.chipClose, color: t.color }}
              title="Quitar tag"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={selected.length === 0 ? '+ Añadir tag' : ''}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
      </div>

      {open && (query.length > 0 || filtered.length > 0) && (
        <div style={styles.popover}>
          {filtered.slice(0, 8).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleSelect(t.id)}
              style={styles.popoverItem}
            >
              <span style={{ ...styles.dot, background: t.color }} />
              <span style={{ flex: 1 }}>{t.nombre}</span>
              <span style={styles.count}>{t.tasksCount > 0 ? t.tasksCount : ''}</span>
            </button>
          ))}
          {query.trim() && !exactMatch && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              style={{ ...styles.popoverItem, ...styles.createItem }}
            >
              <span style={{ ...styles.dot, background: DEFAULT_COLOR }} />
              <span style={{ flex: 1 }}>
                {creating ? 'Creando…' : <>Crear <strong>{query.trim()}</strong></>}
              </span>
            </button>
          )}
          {filtered.length === 0 && (!query.trim() || exactMatch) && (
            <div style={styles.empty}>
              {allTags.length === 0
                ? 'Empieza a escribir para crear tu primera tag.'
                : 'Sin coincidencias.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    position: 'relative',
    width: '100%',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    minHeight: 42,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid',
  },
  chipClose: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    padding: 0,
    lineHeight: 1,
  },
  input: {
    flex: 1,
    minWidth: 100,
    border: 'none',
    outline: 'none',
    fontSize: 13,
    padding: '4px 2px',
    background: 'transparent',
  },
  popover: {
    position: 'absolute',
    bottom: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
    zIndex: 50,
    maxHeight: 240,
    overflowY: 'auto',
  },
  popoverItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: '#0f172a',
    textAlign: 'left',
  },
  createItem: {
    borderTop: '1px solid #f1f5f9',
    color: '#1d4ed8',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  count: {
    fontSize: 11,
    color: '#94a3b8',
  },
  empty: {
    padding: '10px 12px',
    color: '#94a3b8',
    fontSize: 12,
  },
};
